import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Row, Col, Badge, Alert, Tabs, Tab } from 'react-bootstrap';
import { useTheme } from '../contexts/ThemeContext';

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [ssoConnections, setSsoConnections] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showSSOModal, setShowSSOModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'bookkeeper',
    is_active: true
  });

  const [newSSO, setNewSSO] = useState({
    provider: '',
    provider_id: '',
    access_token: '',
    refresh_token: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchSSOConnections();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users');
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  const fetchSSOConnections = async () => {
    try {
      const response = await fetch('/api/admin/sso-connections');
      if (response.ok) {
        const data = await response.json();
        setSsoConnections(data.connections || []);
      }
    } catch (err) {
      setError('Failed to fetch SSO connections');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        setSuccess('User created successfully');
        setNewUser({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'bookkeeper',
          is_active: true
        });
        setShowUserModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to create user');
      }
    } catch (err) {
      setError('Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingUser)
      });

      if (response.ok) {
        setSuccess('User updated successfully');
        setEditingUser(null);
        setShowUserModal(false);
        fetchUsers();
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to update user');
      }
    } catch (err) {
      setError('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('User deleted successfully');
        fetchUsers();
      } else {
        setError('Failed to delete user');
      }
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/toggle-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        setSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchUsers();
      } else {
        setError('Failed to update user status');
      }
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  const handlePasswordChange = (user) => {
    setPasswordChangeUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${passwordChangeUser.id}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        setSuccess('Password changed successfully');
        setShowPasswordModal(false);
        setNewPassword('');
        setPasswordChangeUser(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'admin': { variant: 'danger', icon: 'crown' },
      'bookkeeper': { variant: 'primary', icon: 'user' }
    };
    const config = roleConfig[role] || roleConfig['bookkeeper'];
    return (
      <Badge bg={config.variant}>
        <i className={`fas fa-${config.icon} me-1`}></i>
        {role.toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (isActive) => {
    return (
      <Badge bg={isActive ? 'success' : 'secondary'}>
        <i className={`fas fa-${isActive ? 'check' : 'times'} me-1`}></i>
        {isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  const getSSOStatusBadge = (status) => {
    const statusConfig = {
      'active': { variant: 'success', icon: 'check-circle' },
      'expired': { variant: 'warning', icon: 'exclamation-triangle' },
      'revoked': { variant: 'danger', icon: 'times-circle' }
    };
    const config = statusConfig[status] || statusConfig['active'];
    return (
      <Badge bg={config.variant}>
        <i className={`fas fa-${config.icon} me-1`}></i>
        {status.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-users-cog me-2"></i>
        Admin Panel - User Management
      </h3>

      {error && (
        <Alert variant="danger" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-3">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      <Tabs defaultActiveKey="users" className="mb-3">
        <Tab eventKey="users" title="Users">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>User Management</span>
              <Button
                variant="primary"
                onClick={() => {
                  setEditingUser(null);
                  setShowUserModal(true);
                }}
              >
                <i className="fas fa-plus me-1"></i>
                Add User
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table striped hover className="mb-0">
                <thead style={{ background: colors.primary, color: 'white' }}>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.first_name} {user.last_name}</td>
                      <td>{user.email}</td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{getStatusBadge(user.is_active)}</td>
                      <td>
                        {user.last_login 
                          ? new Date(user.last_login).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => {
                            setEditingUser(user);
                            setShowUserModal(true);
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-1"
                          onClick={() => handlePasswordChange(user)}
                        >
                          <i className="fas fa-key"></i>
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-1"
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                        >
                          <i className={`fas fa-${user.is_active ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="sso" title="SSO Connections">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>SSO & 3rd Party Connections</span>
              <Button
                variant="primary"
                onClick={() => setShowSSOModal(true)}
              >
                <i className="fas fa-plus me-1"></i>
                Add Connection
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table striped hover className="mb-0">
                <thead style={{ background: colors.secondary, color: 'white' }}>
                  <tr>
                    <th>User</th>
                    <th>Provider</th>
                    <th>Provider ID</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ssoConnections.map((connection) => (
                    <tr key={connection.id}>
                      <td>{connection.user_email}</td>
                      <td>
                        <Badge bg="info">
                          <i className={`fab fa-${connection.provider.toLowerCase()} me-1`}></i>
                          {connection.provider.toUpperCase()}
                        </Badge>
                      </td>
                      <td>{connection.provider_id}</td>
                      <td>{getSSOStatusBadge(connection.status)}</td>
                      <td>
                        {connection.expires_at 
                          ? new Date(connection.expires_at).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            // Handle revoke connection
                            console.log('Revoke connection:', connection.id);
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user me-2"></i>
            {editingUser ? 'Edit User' : 'Add New User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingUser?.first_name || newUser.first_name}
                    onChange={(e) => {
                      if (editingUser) {
                        setEditingUser({ ...editingUser, first_name: e.target.value });
                      } else {
                        setNewUser({ ...newUser, first_name: e.target.value });
                      }
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingUser?.last_name || newUser.last_name}
                    onChange={(e) => {
                      if (editingUser) {
                        setEditingUser({ ...editingUser, last_name: e.target.value });
                      } else {
                        setNewUser({ ...newUser, last_name: e.target.value });
                      }
                    }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editingUser?.email || newUser.email}
                    onChange={(e) => {
                      if (editingUser) {
                        setEditingUser({ ...editingUser, email: e.target.value });
                      } else {
                        setNewUser({ ...newUser, email: e.target.value });
                      }
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={editingUser?.role || newUser.role}
                    onChange={(e) => {
                      if (editingUser) {
                        setEditingUser({ ...editingUser, role: e.target.value });
                      } else {
                        setNewUser({ ...newUser, role: e.target.value });
                      }
                    }}
                  >
                    <option value="bookkeeper">Bookkeeper</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {!editingUser && (
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active User"
                checked={editingUser?.is_active ?? newUser.is_active}
                onChange={(e) => {
                  if (editingUser) {
                    setEditingUser({ ...editingUser, is_active: e.target.checked });
                  } else {
                    setNewUser({ ...newUser, is_active: e.target.checked });
                  }
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  {editingUser ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i>
                  {editingUser ? 'Update User' : 'Create User'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Password Change Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-key me-2"></i>
            Change Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>User</Form.Label>
              <Form.Control
                type="text"
                value={passwordChangeUser ? `${passwordChangeUser.first_name} ${passwordChangeUser.last_name} (${passwordChangeUser.email})` : ''}
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (minimum 6 characters)"
                minLength={6}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={changePassword} disabled={!newPassword || newPassword.length < 6}>
            Change Password
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminPanel;
